import { Button, HorizontalDivider, Typography } from '@smartgift/frontend-toolkit';
import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { CardContent } from '@hero/atoms';
import { AvailableCharitiesModal } from '@hero/components/modals';
import {
  NON_EDITABLE_RECOGNITION_STATUS,
  PAYMENT_METHOD_TYPES,
  PRODUCT_TYPES,
  RECOGNITION_STATUSES
} from '@hero/constants';
import { useGetCharity } from '@hero/hooks';
import { VerticalListHeader, ProductVerticalList } from '@hero/molecules';
import { EditableProductsModal, CharityVerticalListHeader } from '@hero/organisms';
import {
  useGetRecognition,
  useUpdateRecognition,
  useGetRecognitionProducts
} from '@hero/services/order-service/recognition/hooks';
import { useNotification } from '@hero/store/hooks';
import { hasAnyAvailableProducts } from '@hero/utils';

import * as S from './style';

function RecognitionGiftsAccordion({ refetchRecognition }) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { charityEnabled, isCharityExist } = useGetCharity();
  const { data: allProductsResponse, isFetching: isFetchingProducts } = useGetRecognitionProducts(
    recognitionCode,
    {
      onSuccess: data => {
        if (!hasAnyAvailableProducts(data)) {
          showNotification({
            title: (
              <Trans
                key="noAvailableProducts"
                i18nKey="noAvailableProducts"
                values={{ feature: t('oneTimeGift').toLocaleLowerCase() }}
              />
            ),
            message: (
              <Trans
                key="noAvailableProductsDesc"
                i18nKey="noAvailableProductsDesc"
                values={{ feature: t('oneTimeGift').toLocaleLowerCase() }}
              />
            ),
            primaryButtonProps: { children: t('editGiftCollection') },
            onSubmit: () => {
              setShowProductModal(true);
            }
          });
        }
      }
    }
  );

  const allProducts = allProductsResponse || [];

  const [showProductModal, setShowProductModal] = useState(false);
  const [showCharitiesModal, setShowCharitiesModal] = useState(false);

  const { data: recognition, isFetching: isFetchingRecognition } = useGetRecognition({
    recognitionCode
  });
  const { mutateAsync: updateRecognition, isLoading: isUpdatingProducts } = useUpdateRecognition({
    options: {
      onSuccess: () => {
        if (refetchRecognition) refetchRecognition();
      }
    }
  });

  const isDetailItemEditable = useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const products = useMemo(() => {
    return allProducts.filter(p => p?.meta?.productType !== PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const charities = useMemo(() => {
    return allProducts.filter(p => p?.meta?.productType === PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const isLiveFeature = recognition?.status?.current === RECOGNITION_STATUSES.live;

  const selectedProducts = useMemo(() => {
    return allProducts?.filter(p => p?.meta?.productType !== PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const selectedCharities = useMemo(() => {
    return allProducts?.filter(p => p?.meta?.productType === PRODUCT_TYPES.CHARITY);
  }, [allProducts]);

  const unavailableProducts = uniq(recognition?.products)?.filter(
    p => !products?.map(product => product.merchantProductId).includes(p.merchantProductId)
  );

  const updateProducts = (
    products,
    successMessage = t('giftsSuccessfullyAdded', { featureType: 'oneTimeGift' })
  ) => {
    updateRecognition({
      params: { recognitionCode },
      body: {
        products: uniq([...products, ...selectedCharities])
      },
      successMessage
    });
  };

  const updateCharities = charities =>
    updateRecognition({
      params: { recognitionCode },
      body: {
        products: uniq([...selectedProducts, ...charities])
      },
      successMessage: t('charitySuccessfullyAdded', { featureType: 'oneTimeGift' })
    });

  const onRemoveItem = merchantProductId => {
    if (selectedProducts.length > 0) {
      const product =
        selectedProducts.find(item => item.merchantProductId === merchantProductId) ??
        recognition?.products?.find(item => item?.merchantProductId === merchantProductId);

      const successMessage = t('successfullyRemovedMessage', { name: product?.name });
      updateRecognition({
        params: { recognitionCode },
        body: {
          products: [...selectedProducts, ...selectedCharities].filter(
            item => item.merchantProductId !== merchantProductId
          )
        },
        successMessage
      });
    }
  };

  const closeProductModal = () => {
    setShowProductModal(false);
  };

  return (
    <S.GiftsWrapper>
      <CardContent>
        <VerticalListHeader
          updateProducts={updateProducts}
          products={selectedProducts}
          setShowProductModal={setShowProductModal}
          isDetailItemEditable={isDetailItemEditable}
          isLiveFeature={isLiveFeature}
          rearrangeEffectiveType={t('reminderSends')}
          isFetching={isFetchingProducts || isFetchingRecognition}
        />
        {recognition?.products.filter(p => p.meta?.productType !== PRODUCT_TYPES.CHARITY)?.length >
          0 && (
          <ProductVerticalList
            products={{ docs: products }}
            creationProducts={recognition?.products.filter(
              p => p.meta?.productType !== PRODUCT_TYPES.CHARITY
            )}
            selectedProducts={selectedProducts}
            isFetching={isFetchingProducts || isUpdatingProducts}
            onRemoveProduct={onRemoveItem}
            isProductsRemovable={isDetailItemEditable && selectedProducts?.length > 1}
            unavailableProducts={unavailableProducts}
            updateProducts={updateProducts}
            featureStatus={recognition?.status?.current}
          />
        )}
        {charityEnabled && isCharityExist && (
          <>
            <HorizontalDivider $margin="24px 0" />
            <CharityVerticalListHeader
              collectionSize={charities?.length}
              setShowCharitiesModal={setShowCharitiesModal}
              isDetailItemEditable={isDetailItemEditable}
              isFetching={isFetchingProducts || isFetchingRecognition}
            />
            {charities.length ? (
              <ProductVerticalList
                products={{ docs: charities }}
                creationProducts={recognition?.products.filter(
                  p => p.meta?.productType === PRODUCT_TYPES.CHARITY
                )}
                selectedProducts={selectedCharities}
                isFetching={isFetchingProducts || isUpdatingProducts}
                onRemoveProduct={onRemoveItem}
                isProductsRemovable={isDetailItemEditable && selectedProducts?.length > 1}
                updateProducts={updateCharities}
                featureStatus={recognition?.status?.current}
              />
            ) : (
              <S.EmptySelectedCharityPlaceholderWrapper>
                <Typography variant="body2">{t('addCharityPlaceholderText')}</Typography>
                <Button
                  onClick={() => setShowCharitiesModal(true)}
                  disabled={recognition?.status?.current === RECOGNITION_STATUSES.expired}
                >
                  {t('addACharitableGiving')}
                </Button>
              </S.EmptySelectedCharityPlaceholderWrapper>
            )}
          </>
        )}
        <HorizontalDivider $margin="24px 0" />
        <S.Description variant="body2SemiBold" color="neutral" emphasis="500">
          {t('verticalListSubTitle')}
        </S.Description>
        {recognition && showProductModal && (
          <EditableProductsModal
            isModalOpen={showProductModal}
            onRequestClose={closeProductModal}
            selectedProducts={selectedProducts}
            updateProducts={updateProducts}
            featureType="recognition"
            isLiveFeature={isLiveFeature}
            isDraft={recognition.status.current === RECOGNITION_STATUSES.draft}
            isCCPayment={recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard}
          />
        )}
        {charityEnabled && recognition && showCharitiesModal && (
          <AvailableCharitiesModal
            isOpen={showCharitiesModal}
            selectedCharities={selectedCharities}
            setSelectedCharities={updateCharities}
            setIsOpen={setShowCharitiesModal}
            saveWithoutProducts={true}
          />
        )}
      </CardContent>
    </S.GiftsWrapper>
  );
}

RecognitionGiftsAccordion.propTypes = {
  refetchRecognition: PropTypes.func
};

export { RecognitionGiftsAccordion };
